import React, { forwardRef, useState, useEffect, useRef } from 'react';
import { Button, Form, Input, Radio, message } from 'tea-component';
import chatGPTIcon from '@sg/assests/svg/chatgpt-icon.svg';
import deepSeekIcon from '@sg/assests/svg/deepseek-icon.svg';
import ICON_REGENERATE from '@sg/assests/svg/regenerate-icon.svg';
import TinyEditor from '@sg/components/TinyEditor';
import UploadFileList from '@sg/components/UploadFileList';
import './index.scss';

function TableFormSummary(props: any, ref: any) {
    const {
        data,
        onChange,
        showTitleInput = true,
        defaultTitle = "", // Default title when data.title is empty
        meetingId,
        source,
        uniqueId = "ai-summary-upload", // Default fallback
        domain = "weekly_project_highlight", // Domain for COS upload folder
        style,
        hideUploadButton = false,
        excludeEdit = false,
        excludeDelete = false,
        ...rest
    } = props;

    // 用来跟踪悬浮的编辑图标
    // Normalize: Accept both 'content' and 'description' props, internally use 'content'
    const [tableData, setTableData] = useState(() => {
        const normalizedData = {
            ...data,
            title: data?.title || defaultTitle, // Use defaultTitle only if data.title is empty
            // If 'description' is provided but 'content' is not, use 'description' as 'content'
            content: data?.content || data?.description || '',
        };
        return normalizedData;
    });

    // 用来标记组件是否是第一次加载
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    // Lazy load TinyEditor - only render when component is visible or focused
    const [shouldRenderEditor, setShouldRenderEditor] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);

    useEffect(() => {
        // 组件第一次加载时不触发 onChange
        if (isFirstLoad) {
            setIsFirstLoad(false);
        }
    }, [isFirstLoad]);

    // Intersection Observer to detect when component is visible
    useEffect(() => {
        if (!containerRef.current) return;

        // If already rendering editor, no need to observe
        if (shouldRenderEditor) return;

        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setShouldRenderEditor(true);
                        // Disconnect observer once editor should be rendered
                        if (observerRef.current) {
                            observerRef.current.disconnect();
                        }
                    }
                });
            },
            {
                rootMargin: '200px', // Start loading 200px before component is visible
                threshold: 0.01,
            }
        );

        observerRef.current.observe(containerRef.current);

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [shouldRenderEditor]);

    // Fallback: render editor after a short delay if intersection observer isn't supported
    useEffect(() => {
        const timer = setTimeout(() => {
            if (!shouldRenderEditor) {
                setShouldRenderEditor(true);
            }
        }, 100); // Small delay to allow intersection observer to work first

        return () => clearTimeout(timer);
    }, [shouldRenderEditor]);

    // 处理字段更新
    const handleFieldChange = (field: string, value: any) => {
        if (isFirstLoad) return; // 如果是第一次加载，阻止触发 onChange
        setTableData((prevData: any) => {
            const updatedData = { ...prevData, [field]: value };
            onChange(updatedData); // 只有不是第一次加载时才触发 onChange
            return updatedData;
        });
    };

    // 上传文件成功回调
    const handleAiFilesChange = (updatedFiles: any) => {
        if (JSON.stringify(updatedFiles) !== JSON.stringify(tableData?.files)) {
            handleFieldChange('files', updatedFiles);
        }
    };

    // Regenerate function - placeholder for future enhancement
    const handleRegenerate = () => {
        console.log('Regenerate button clicked - Future enhancement');
        console.log('Current data:', tableData);
        // TODO: Implement AI regeneration logic here
    };

    // 格式化内容为带小圆点的行
    const formatContent = (content: any) => {
        if (Array.isArray(content)) {
            return `<ul>\n` + content
                .map((item: string) => `<li>${item}</li>`)
                .join('\n') + `\n</ul>`;
        }
        return content;
    };

    return (
        <div ref={containerRef}>
            <Form hideLabel={true} style={{ width: '100%', ...style }} className='dbs-table-form-summary'>
               <Radio.Group
                    value={tableData?.agent}
                    onChange={(value) => handleFieldChange('agent', value)}
                    style={{ marginTop: 14, marginBottom: 0 }}
                >
                    {/* The chatgpt and deepseek radio buttons are temporarily disabled because the AI summary is not supported for now */}
                    <Radio name="chatGPT" disabled>
                        <img src={chatGPTIcon} alt="ChatGPT" />
                        ChatGPT
                    </Radio>
                    <Radio name="deepSeek" disabled>
                        <img src={deepSeekIcon} alt="DeepSeek" />
                        DeepSeek
                    </Radio>
                    <Radio name="text" value>
                        Text
                    </Radio>
                </Radio.Group>
                {showTitleInput && (<>
                    <Form.Item>
                        <Input
                            size="full"
                            value={tableData?.title || ''}
                            onChange={(value) => handleFieldChange('title', value)}
                            placeholder="Please enter title"
                        />
                    </Form.Item>
                </>)}

                <Form.Item>
                    {shouldRenderEditor ? (
                        <TinyEditor
                            value={formatContent(tableData?.content)}
                            getEditorContent={(value) => handleFieldChange('content', value)}
                            isShowToolbar={true}
                            minHeight={160}
                            maxContentHeight={400}
                            placeholder="Please enter text"
                        />
                    ) : (
                        <div style={{ minHeight: 160, padding: '8px', border: '1px solid #d9d9d9', borderRadius: '4px', backgroundColor: '#fafafa' }}>
                            <div style={{ color: '#999', fontSize: '14px' }}>Loading editor...</div>
                        </div>
                    )}
                </Form.Item>

                <UploadFileList
                    autoResizeInput={true}
                    showFileList={true}
                    fileListPosition="top"
                    uniqueId={uniqueId}
                    uploadFolderName={domain}
                    defaultList={tableData?.files}
                    multiple={true}
                    maxSize={5 * 1024 * 1024 * 1024} // 5GB
                    onResult={handleAiFilesChange}
                    buttonTips=""
                    hideUploadButton={hideUploadButton}
                    excludeEdit={excludeEdit}
                    excludeDelete={excludeDelete}
                    // 先注销掉  等做了另外俩部分再根据条件展示与隐藏
                    // extraButtons={
                    //     <Button onClick={handleRegenerate}>
                    //         <img
                    //             style={{
                    //                 position: 'relative',
                    //                 top: -2,
                    //                 marginRight: 4,
                    //                 width: 16,
                    //                 height: 16,
                    //                 verticalAlign: 'middle',
                    //                 color: '#888',
                    //             }}
                    //             src={ICON_REGENERATE}
                    //         />
                    //         <span>Regenerate</span>
                    //     </Button>
                    // }
                />
            </Form>
        </div>
    );
}

export default forwardRef(TableFormSummary);
